import { capitalize, pluralizeWithCount } from "@common/helpers/string";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BillQueryParams } from "@common/contracts/bill.contract";
import {
    BillDueDateLabels,
    BillDueDateSubsegment,
    BillStats as BillStatsType,
    BillStatSubsegment,
    BillStatus,
} from "@common/types/bill";
import { SegmentBar } from "@design-system/SegmentBar/SegmentBar";
import { GradientColors, Segment } from "@helpers/segment";
import styles from "./BillStats.module.scss";
import { BillStatTooltip } from "./BillStatTooltip";

interface Props {
    billStats?: BillStatsType;
    filters: BillQueryParams["filters"];
    setFilters: (filters: BillQueryParams["filters"]) => void;
}

const statusGradientColors: Record<keyof BillStatsType, GradientColors> = {
    draft: {
        start: "#FAFBFC",
        end: "#D9DDE2",
        subtitle: "#515D71",
    },
    open: {
        start: "#FAE9D9",
        end: "#F9CAA1",
        subtitle: "#A86534",
    },
    paid: {
        start: "#E2F0FB",
        end: "#94C9F1",
        subtitle: "#115F9E",
    },
    void: {
        start: "#EBE5FD",
        end: "#B8A4F1",
        subtitle: "#5B40AB",
    },
};

export const BillStats: React.FC<Props> = ({
    billStats,
    filters,
    setFilters,
}) => {
    const [stats, setStats] = useState<BillStatsType | null>(billStats ?? null);

    // HACK: this is a hack to prevent major re-renders when the query changes
    useEffect(() => {
        if (!billStats) {
            return;
        }
        setStats(billStats);
    }, [billStats]);

    const segments = useMemo(() => {
        if (!stats) {
            return [];
        }

        return Object.entries(stats).map(([id, stat]) => {
            const baseSegment: Segment = {
                id,
                label: capitalize(id),
                value: stat.total,
                colors: statusGradientColors[id as keyof BillStatsType],
                subtitle: pluralizeWithCount("bill", stat.count),
                subsegments: stat.subsegments?.map(
                    (subsegment: BillStatSubsegment) => ({
                        id: subsegment.label,
                        label: BillDueDateLabels[subsegment.label],
                        value: subsegment.total,
                        subtitle: pluralizeWithCount("bill", subsegment.count),
                        tooltip: (
                            <BillStatTooltip
                                status={id as BillStatus}
                                label={BillDueDateLabels[subsegment.label]}
                                stat={subsegment}
                                entityStats={subsegment.entities}
                            />
                        ),
                    }),
                ),
                tooltip: (
                    <BillStatTooltip status={id as BillStatus} stat={stat} />
                ),
            };

            return baseSegment;
        });
    }, [stats]);

    const filterByDueDateSubsegment = useCallback(
        (subsegment: BillDueDateSubsegment) => {
            const newFilters: BillQueryParams["filters"] = {
                ...filters,
            };

            // Set due date range based on the subsegment
            switch (subsegment) {
                case BillDueDateSubsegment.PAST_DUE_1_TO_30:
                    newFilters.minDue = undefined;
                    newFilters.maxDue = 30;
                    break;
                case BillDueDateSubsegment.PAST_DUE_31_TO_60:
                    newFilters.minDue = 31;
                    newFilters.maxDue = 60;
                    break;
                case BillDueDateSubsegment.PAST_DUE_61_PLUS:
                    newFilters.minDue = 61;
                    newFilters.maxDue = undefined;
                    break;
                case BillDueDateSubsegment.OVERDUE:
                    newFilters.overdue = true;
            }

            setFilters(newFilters);
        },
        [filters, setFilters],
    );

    const filterByStatus = useCallback(
        (status: BillStatus) => {
            const newFilters: BillQueryParams["filters"] = {
                ...filters,
            };

            newFilters.externalBillStatuses = [status];
            setFilters(newFilters);
        },
        [filters, setFilters],
    );

    const onSegmentClick = useCallback(
        (segmentId: string) => {
            if (
                Object.values(BillDueDateSubsegment).includes(
                    segmentId as BillDueDateSubsegment,
                )
            ) {
                filterByDueDateSubsegment(segmentId as BillDueDateSubsegment);
            } else if (
                Object.values(BillStatus).includes(segmentId as BillStatus)
            ) {
                filterByStatus(segmentId as BillStatus);
            }
        },
        [filterByDueDateSubsegment, filterByStatus],
    );

    return (
        <div className={styles.billStatsContainer}>
            <SegmentBar
                className={styles.billStats}
                segments={segments}
                formatValueAsMoney
                onSegmentClick={onSegmentClick}
            />
        </div>
    );
};
